import React from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {mostraQuota, formattaOra, stessoGiorno} from '../../utils/formato';
import {ascolta} from '../../preferenze';
import {prossimePartite, descriviErrore} from '../../services/vetrinaApi';
import {mettiInSchedina, inSchedina} from './schedina';
import {coloreSport} from '../common/coloriSport';
import IconaSport from '../common/IconaSport';
import Caricamento from '../common/Caricamento';
import Nota from './Nota';
import {inAnteprima} from '../../skin';

/* La quota nel formato di chi guarda, come nella griglia di Sport. */
const quota = (v) => mostraQuota(v);

/**
 * LE PROSSIME PARTITE: la striscia che scorre da sola.
 *
 * COSA FA: mostra le partite che cominciano prima, di tutti gli sport, con
 * l'esito finale cliccabile. Scorre da sola una scheda alla volta; si ferma
 * quando ci passi sopra col mouse, la tocchi, o ci metti il fuoco con la
 * tastiera; si puo' sempre scorrere a mano, con le frecce o col dito. Non
 * scorre se il dispositivo chiede meno movimento (prefers-reduced-motion).
 * Toccando una quota si va in Sport con quella quota gia' in schedina.
 * I segni (1, X, 2) e il loro ordine sono quelli della SCHEDA MERCATI dello
 * sport, decisi nel backoffice (Sito, schede): il catalogo li manda con ogni
 * esito e qui si scrivono come arrivano, come fa la griglia di Sport.
 *
 * COSA NON FA: non segue i preferiti, non ordina per giocate - e' l'orario e
 * basta. Non e' un elenco completo: per il resto si va in Sport.
 *
 * DAL BACKOFFICE (DA FARE, pagina «Home» sotto Sito, per skin): quante
 * schede, quali sport entrano, ogni quanti secondi scorre. Finche' non
 * esiste, i valori stanno qui sotto come prop con un predefinito, e sono
 * scritti in un posto solo.
 *
 * LE PAROLE passano da t('…') con la frase italiana come chiave (i18n.js):
 * nelle altre lingue arrivano dal dizionario. I nomi di squadre e campionati
 * arrivano dal catalogo come sono.
 */
export default function ProssimePartite({quante = 12, ogniSecondi = 5}) {
  const navigate = useNavigate();
  const {t} = useTranslation();
  const [stato, setStato] = React.useState({partite: null, errore: ''});
  const [sportScelto, setSportScelto] = React.useState('');
  const [fermo, setFermo] = React.useState(false);
  const [, ridisegna] = React.useState(0);
  React.useEffect(() => ascolta(() => ridisegna((n) => n + 1)), []);
  const striscia = React.useRef(null);

  React.useEffect(() => {
    let vivo = true;
    prossimePartite(quante)
        .then((p) => vivo && setStato({partite: p, errore: ''}))
        .catch((e) => vivo && setStato({partite: [], errore: descriviErrore(e)}));
    return () => { vivo = false; };
  }, [quante]);

  const partite = stato.partite || [];
  const sport = [];
  partite.forEach((p) => {
    if (p.sportId && !sport.some((s) => s.id === p.sportId)) {
      sport.push({id: p.sportId, name: p.sportName || ''});
    }
  });
  const mostrate = sportScelto ? partite.filter((p) => p.sportId === sportScelto) : partite;

  /* Lo scorrimento automatico: una scheda alla volta, e in fondo si torna
     in cima. Il passo si misura dalla prima scheda, cosi' telefono e
     computer usano lo stesso codice con misure diverse. */
  React.useEffect(() => {
    const el = striscia.current;
    if (!el || fermo || mostrate.length < 2) return undefined;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }
    const t = setInterval(() => {
      const scheda = el.firstElementChild;
      if (!scheda) return;
      const passo = scheda.getBoundingClientRect().width + 12;
      const fine = el.scrollWidth - el.clientWidth - 4;
      if (el.scrollLeft >= fine) el.scrollTo({left: 0, behavior: 'smooth'});
      else el.scrollBy({left: passo, behavior: 'smooth'});
    }, Math.max(2, ogniSecondi) * 1000);
    return () => clearInterval(t);
  }, [fermo, mostrate.length, ogniSecondi]);

  const scorri = (verso) => {
    const el = striscia.current;
    if (!el || !el.firstElementChild) return;
    const passo = el.firstElementChild.getBoundingClientRect().width + 12;
    el.scrollBy({left: verso * passo, behavior: 'smooth'});
  };

  const tocca = (partita, mercato, esito, segno) => {
    mettiInSchedina(partita, mercato, esito, segno);
    navigate('/sport');
  };

  return (
    <section className={'vet-blocco'} aria-label={t('Prossime partite')}>
      <div className={'vet-testata'}>
        <h2>{t('Prossime partite')}</h2>
        <span className={'vet-testata-nota'}>{t('scorre da sola · si ferma se la tocchi')}</span>
        <div className={'vet-testata-dx'}>
          <button type={'button'} className={'vet-link'} onClick={() => navigate('/sport')}>
            {t('Vai a Sport')}
          </button>
          <button type={'button'} className={'vet-freccia'} aria-label={t('Indietro')}
            onClick={() => scorri(-1)}>&#8249;</button>
          <button type={'button'} className={'vet-freccia'}
            aria-label={fermo ? t('Riprendi lo scorrimento') : t('Ferma lo scorrimento')}
            aria-pressed={fermo} onClick={() => setFermo((f) => !f)}>
            {fermo ? '▶' : '‖'}
          </button>
          <button type={'button'} className={'vet-freccia'} aria-label={t('Avanti')}
            onClick={() => scorri(1)}>&#8250;</button>
        </div>
      </div>

      {sport.length > 1 && (
        <div className={'vet-filtri lvc-strip'} role={'group'} aria-label={t('Sport')}>
          <button type={'button'} className={'vet-filtro' + (sportScelto ? '' : ' scelto')}
            aria-pressed={!sportScelto} onClick={() => setSportScelto('')}>{t('Tutti')}</button>
          {sport.map((s) => (
            <button type={'button'} key={s.id}
              className={'vet-filtro' + (sportScelto === s.id ? ' scelto' : '')}
              aria-pressed={sportScelto === s.id}
              onClick={() => setSportScelto(s.id)}>
              <span className={'vet-barra-sport'} style={{background: coloreSport(s)}}/>
              {s.name}
            </button>
          ))}
        </div>
      )}

      {stato.partite === null && <Caricamento forma={'partite'} righe={3}/>}

      {stato.errore && (
        /* Il dettaglio tecnico («Errore 404 - Not Found») serve a chi prepara
           la skin, nell'anteprima del backoffice; al giocatore basta sapere
           che adesso non ci sono e che puo' riprovare. */
        <Nota tono={'errore'}>{inAnteprima()
          ? t('Prossime partite non disponibili: {{errore}}', {errore: stato.errore})
          : t('Le prossime partite non si vedono in questo momento. Riprova tra poco.')}</Nota>
      )}

      {stato.partite !== null && !stato.errore && mostrate.length === 0 && (
        <Nota>{t('Nessuna partita in programma al momento.')}</Nota>
      )}

      {mostrate.length > 0 && (
        <div className={'vet-striscia lvc-strip'} ref={striscia}
          onMouseEnter={() => setFermo(true)} onMouseLeave={() => setFermo(false)}
          onTouchStart={() => setFermo(true)} onFocus={() => setFermo(true)}>
          {mostrate.map((p) => <Scheda key={p.id} partita={p} tocca={tocca} t={t}/>)}
        </div>
      )}
    </section>
  );
}

/**
 * Una scheda: campionato, orario, le due squadre, le quote dell'esito
 * finale e il numero degli altri mercati.
 *
 * Le caselle delle quote sono le stesse della griglia della pagina Sport
 * (lvc-cella / lvc-quota / lvc-scelta): stessa cosa, stesso nome.
 */
function Scheda({partita, tocca, t}) {
  const sport = {name: partita.sportName, id: partita.sportId};
  const esitoFinale = (partita.markets || []).find((m) => m.kind === 'MATCH_RESULT');
  /* Gli esiti arrivano gia' nell'ordine della scheda mercati dello sport e
     ognuno porta il suo `segno`: si scrivono come arrivano. Prima qui si
     contava la posizione (primo = 1, secondo = X, terzo = 2), e siccome il
     fornitore manda le squadre in ordine alfabetico col pareggio in fondo,
     su «Sarmiento de Junin - River Plate» la X stava sulla quota di casa e
     il 2 sul pareggio: chi toccava la X giocava un altro esito. Se un esito
     arriva senza segno si scrive il suo nome, mai un segno inventato. */
  const esiti = esitoFinale ? (esitoFinale.selections || []) : [];
  const altri = Math.max(0, (partita.countMarkets || (partita.markets || []).length) - 1);
  const oggi = stessoGiorno(partita.date, new Date().toISOString());

  return (
    <article className={'vet-scheda'}>
      <div className={'vet-scheda-testa'}>
        <span className={'vet-barra-sport'} style={{background: coloreSport(sport)}}/>
        <IconaSport sport={sport} className={'vet-icona-sport'}/>
        <span className={'vet-lega'}>{partita.competitionName || partita.sportName}</span>
        <span className={'vet-ora'}>
          {formattaOra(partita.date, oggi ? 'ora' : 'giorno-ora')}
        </span>
      </div>
      <div className={'vet-squadre'}>
        <span>{partita.home}</span>
        <span>{partita.away}</span>
      </div>
      <div className={'vet-quote'}>
        {esiti.map((e) => {
          const scelta = inSchedina(partita, esitoFinale, e);
          const segno = e.segno || e.name;
          return (
            <button type={'button'} key={e.id}
              className={'lvc-cella lvc-quota' + (scelta ? ' lvc-scelta' : '')}
              aria-pressed={scelta}
              aria-label={t('{{segno}} a {{quota}}', {segno, quota: quota(e.odd)})}
              onClick={() => tocca(partita, esitoFinale, e, segno)}>
              <span className={'vet-segno'}>{segno}</span>
              <span className={'vet-valore'}>{quota(e.odd)}</span>
            </button>
          );
        })}
        {esiti.length === 0 && <Nota piccola>{t('Esito finale non disponibile')}</Nota>}
        {altri > 0 && (
          <button type={'button'} className={'vet-altri'}
            aria-label={t('Altri {{count}} mercati', {count: altri})}
            onClick={() => tocca(partita, null, null)}>
            +{altri}
          </button>
        )}
      </div>
    </article>
  );
}
